import React from 'react';
import { Box, Hidden } from '@doist/marketist';
import { StructuredDataMarkup } from 'components/blog-posts/structured-data-markup';
import { ContainerSidebar } from 'components/container-sidebar/container-sidebar';
import { NotFound } from 'components/error/not-found';
import Footer from 'components/Footer/Footer';
import Header from 'components/header/header';
import Seo from 'components/Seo/Seo';
import { Prefooter } from 'components/templates/prefooter/prefooter';
import { MainDetails } from 'components/templates/template-details/main-details/main-details';
import { PageHeader } from 'components/templates/template-details/page-header/page-header';
import { SideDetails } from 'components/templates/template-details/side-details/side-details';
import { SimilarTemplates } from 'components/templates/template-details/similar-templates/similar-templates';
import { getAllLanguages } from 'i18n/constants';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getTemplateData, getTemplates } from 'services/templates/api';
import type { Template, TemplatePreview } from 'services/templates/types';
import type { GetStaticPropsContext, Transifex } from 'types';
type PageProps = {
  notFound?: boolean;
  template: Template;
  relatedTemplates: TemplatePreview[];
  previousTemplateId?: string;
  nextTemplateId?: string;
};
export default function IndividualTemplatePage({
  notFound,
  template,
  previousTemplateId,
  nextTemplateId,
  relatedTemplates
}: PageProps) {
  const {
    t,
    i18n
  } = useTranslation('templates');
  if (notFound) return <NotFound />;
  const languagePath = i18n.language === 'en' ? '' : `/${i18n.language}`;
  return <>
            <Seo path={`/templates/${template.id}`} title={template.seo.title} description={template.seo.description} images={[{
      url: `${process.env.NEXT_PUBLIC_BASE_URL}/${i18n.language}/templates-ogimages/${template.id}`,
      width: 1200,
      height: 628,
      alt: template.seo.title
    }]} data-sentry-element="Seo" data-sentry-source-file="[slug].tsx" />
            <StructuredDataMarkup type="Article" title={template.name} authors={[{
      type: 'Person',
      name: template.creator.name
    }]} images={[template.thumbnail_image]} breadcrumb={[{
      name: t('allTemplates'),
      item: `${process.env.NEXT_PUBLIC_BASE_URL}${languagePath}/templates`
    }]} data-sentry-element="StructuredDataMarkup" data-sentry-source-file="[slug].tsx" />
            <Box style={{
      backgroundColor: 'var(--composition-background-neutral)'
    }} data-sentry-element="Box" data-sentry-source-file="[slug].tsx">
                <Header data-sentry-element="Header" data-sentry-source-file="[slug].tsx" />
                <PageHeader template={template} previousTemplateId={previousTemplateId} nextTemplateId={nextTemplateId} data-sentry-element="PageHeader" data-sentry-source-file="[slug].tsx" />
                <ContainerSidebar width="lg" className="mbe-64" data-sentry-element="ContainerSidebar" data-sentry-source-file="[slug].tsx">
                    <SideDetails template={template} data-sentry-element="SideDetails" data-sentry-source-file="[slug].tsx" />
                    <Hidden below="lg" data-sentry-element="Hidden" data-sentry-source-file="[slug].tsx">
                        <MainDetails template={template} relatedTemplates={relatedTemplates} data-sentry-element="MainDetails" data-sentry-source-file="[slug].tsx" />
                    </Hidden>
                </ContainerSidebar>
                <Hidden above="lg" data-sentry-element="Hidden" data-sentry-source-file="[slug].tsx">
                    {relatedTemplates.length > 0 ? <SimilarTemplates templates={relatedTemplates} /> : null}
                </Hidden>
                <Prefooter data-sentry-element="Prefooter" data-sentry-source-file="[slug].tsx" />
                <Footer utm_campaign="templates" backgroundColor="#fff6f0" data-sentry-element="Footer" data-sentry-source-file="[slug].tsx" />
            </Box>
        </>;
}
export async function getStaticProps({
  params
}: GetStaticPropsContext) {
  const slug = params?.slug;
  const locale = params?.locale as Transifex.Languages;
  const [templateData, {
    _nextI18Next
  }] = await Promise.all([getTemplateData(locale, process.env.BUILD_ENV === '1'), serverSideTranslations(locale, ['common', 'templates'])]);
  const template = templateData.templates.find(t => t.id === slug);
  if (!template) {
    return {
      props: {
        _nextI18Next,
        notFound: true
      },
      revalidate: 3600
    };
  }
  let previousTemplateId: string | null = null;
  let nextTemplateId: string | null = null;
  const relatedTemplates: TemplatePreview[] = [];
  for (const cat of templateData.categories) {
    const idx = cat.templates.findIndex(t => t.id === slug);
    if (idx >= 0) {
      if (idx > 0) {
        previousTemplateId = cat.templates[idx - 1].id;
      }
      if (idx < cat.templates.length - 1) {
        nextTemplateId = cat.templates[idx + 1].id;
      }
      relatedTemplates.push(...cat.templates.filter(t => t.id !== template.id).slice(0, 3));
      break;
    }
  }
  return {
    props: {
      _nextI18Next,
      template,
      previousTemplateId,
      nextTemplateId,
      relatedTemplates
    },
    revalidate: 3600
  };
}
export async function getStaticPaths() {
  if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
    return {
      paths: [],
      fallback: 'blocking'
    };
  }
  const locales = getAllLanguages();
  const templatePaths = await Promise.all(locales.map(async locale => {
    const templates = await getTemplates(locale, undefined, process.env.BUILD_ENV === '1');
    return templates.map(template => ({
      params: {
        slug: template.id,
        locale
      }
    }));
  }));
  return {
    paths: templatePaths.flat(),
    fallback: 'blocking'
  };
}